import {useEffect, useRef} from "react";
import {couponDiscountsLeft, type CurrentLadderGift} from "./currentLadderOffer";
import type {useCurrentLadderGiftActions} from "./useCurrentLadderGiftActions";
import {couponCountdown, type CouponCountdown} from "./couponCountdown";
import {CouponTicket} from "./CouponTicket";

export function CurrentLadderGiftTag({gift, actions, countdown, onExpired}: {
	gift: CurrentLadderGift; actions: ReturnType<typeof useCurrentLadderGiftActions>; countdown: CouponCountdown; onExpired: () => void;
}) {
	const {claimed, claim, present} = actions;
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => present(), [present]);
	const openClaim = () => {
		if (couponCountdown(gift.offer.expiresAt, Date.now()).expired) { onExpired(); return; }
		// Hanging beside the card, keep the frame rather than the ticket: expiry may remove the original
		// return target.
		const frame = root.current?.closest("[data-peel-frame]");
		// On a phone the ticket lives in the dialog its tab opens (ui#3343), and the claim dialog opens over
		// that one. Closing the claim returns to the ticket's own reopen control, which replaced the torn
		// stub that opened it. Expiry may remove that control, then the ticket's whole dialog with its tab, so
		// the dialog's first control (its ✕) is next, and the card's own first control is the last resort:
		// the dialog is a portal, so the card is found in the document rather than above the ticket.
		const dialog = root.current?.closest<HTMLElement>('[role="dialog"]');
		claim(() => requestAnimationFrame(() => {
			const target = frame
				? frame.querySelector<HTMLElement>("[data-peel-focus]") ?? frame.querySelector<HTMLElement>("[data-create-card] button")
				: root.current?.querySelector<HTMLElement>("[data-ticket-reopen]")
					?? (dialog?.isConnected ? dialog.querySelector<HTMLElement>("button") : null)
					?? document.querySelector<HTMLElement>("[data-create-card] button");
			target?.focus({preventScroll: true});
		}));
	};
	return <div ref={root}>
		<CouponTicket gift={gift} discountsLeft={couponDiscountsLeft(gift)} claimed={claimed} countdown={countdown} onClaim={openClaim}/>
	</div>;
}
